import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Auth } from '@freelancer/auth';
import {
  Datastore,
  DatastoreCollection,
  OrderByDirection,
  ORDER_BY_OPTIONS_UNICODE_CI,
  RequestError,
} from '@freelancer/datastore';
import {
  CategoryPageViewUser,
  DiscoverCollection,
  DiscoverCollectionsCollection,
  PredefinedDiscoverCollection,
  PredefinedDiscoverCollectionsCollection,
  ProfileViewUser,
} from '@freelancer/datastore/collections';
import { trackById } from '@freelancer/ui/helpers';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ListItemType } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { isFormControl, toNumber } from '@freelancer/utils';
import { DiscoverCollectionTypeApi } from 'api-typings/users/users';
import * as Rx from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-user-lists-card',
  template: `
    <fl-list
      *ngIf="!(userListsLoading$ | flAsync); else loading"
      flTrackingLabel="UserListSelectList"
      [flTrackingReferenceType]="selectedUser ? 'user_id' : undefined"
      [flTrackingReferenceId]="selectedUser ? selectedUser.id : undefined"
      [type]="listItemType"
      [clickable]="clickable"
    >
      <!-- Favourites -->
      <ng-container *ngIf="favoritesList$ | flAsync as favoritesList">
        <ng-container
          *ngIf="
            listItemType !== ListItemType.CHECKBOX ||
            userListGroup?.get(favoritesList.name) as control
          "
        >
          <fl-list-item
            *ngIf="
              listItemType !== ListItemType.CHECKBOX || isFormControl(control)
            "
            flTrackingLabel="UserListSelected"
            flTrackingReferenceType="collection_id"
            [control]="getFormControl(control)"
            [flTrackingReferenceId]="favoritesList.id"
            (click)="handleClick(control, favoritesList)"
          >
            <app-user-list-card
              [iconColor]="IconColor.PINK"
              [iconName]="'ui-heart-alt-v2'"
              [list]="favoritesList"
              [showListDetails]="showListDetails"
              [showItemCount]="showItemCount"
              [showMoreOptions]="false"
            ></app-user-list-card>
          </fl-list-item>
        </ng-container>
      </ng-container>

      <!-- My Hires -->
      <ng-container *ngIf="myHiresList$ | flAsync as myHiresList">
        <ng-container
          *ngIf="
            !hideMyHires &&
            (listItemType !== ListItemType.CHECKBOX ||
              userListGroup?.get(myHiresList.name)) as control
          "
        >
          <fl-list-item
            *ngIf="
              listItemType !== ListItemType.CHECKBOX || isFormControl(control)
            "
            flTrackingLabel="UserListSelected"
            flTrackingReferenceType="collection_id"
            [control]="getFormControl(control)"
            [flTrackingReferenceId]="myHiresList.id"
            (click)="handleClick(control, myHiresList)"
          >
            <app-user-list-card
              [iconColor]="IconColor.CONTEST"
              [iconName]="'ui-suitcase-outline'"
              [list]="myHiresList"
              [showListDetails]="showListDetails"
              [showItemCount]="showItemCount"
              [showMoreOptions]="false"
            ></app-user-list-card>
          </fl-list-item>
        </ng-container>
      </ng-container>

      <!-- Custom Collections -->
      <ng-container
        *ngFor="let customList of customLists$ | flAsync; trackBy: trackById"
        ngProjectAs="fl-list-item"
      >
        <ng-container
          *ngIf="
            listItemType !== ListItemType.CHECKBOX ||
            userListGroup?.get(customList.name) as control
          "
        >
          <fl-list-item
            *ngIf="
              listItemType !== ListItemType.CHECKBOX || isFormControl(control)
            "
            flTrackingLabel="UserListSelected"
            flTrackingReferenceType="collection_id"
            [control]="getFormControl(control)"
            [flTrackingReferenceId]="customList.id"
            (click)="handleClick(control, customList)"
          >
            <app-user-list-card
              [iconColor]="IconColor.PRIMARY"
              [iconName]="'ui-list-bullet'"
              [list]="customList"
              [showListDetails]="showListDetails"
              [showItemCount]="showItemCount"
              [showMoreOptions]="showMoreOptions"
            ></app-user-list-card>
          </fl-list-item>
        </ng-container>
      </ng-container>
    </fl-list>
    <ng-template #loading>
      <fl-spinner flTrackingLabel="UserListSelectLoadingSpinner"></fl-spinner>
    </ng-template>
  `,
  styleUrls: ['./user-lists-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserListsCardComponent implements OnInit {
  DiscoverCollectionType = DiscoverCollectionTypeApi;
  FontColor = FontColor;
  IconColor = IconColor;
  IconSize = IconSize;
  isFormControl = isFormControl;
  ListItemType = ListItemType;
  Margin = Margin;
  trackById = trackById;
  TextSize = TextSize;

  @Input() clickable = false;
  @Input() listItemType = ListItemType.CHECKBOX;
  @Input() selectedUser?: ProfileViewUser | CategoryPageViewUser;
  @Input() hideMyHires = false;
  @Input() showItemCount = false;
  @Input() showListDetails = false;
  @Input() showMoreOptions = true;
  // Form group mapping of collection names to form controls
  @Input() userListGroup: FormGroup;

  @Output() select = new EventEmitter<DiscoverCollection>();

  userListsLoading$: Rx.Observable<boolean>;
  userListsError$: Rx.Observable<
    | RequestError<DiscoverCollectionsCollection>
    | RequestError<PredefinedDiscoverCollectionsCollection>
    | undefined
  >;

  listCurator$: Rx.Observable<number>;
  favoritesList$: Rx.Observable<PredefinedDiscoverCollection>;
  myHiresList$: Rx.Observable<PredefinedDiscoverCollection>;
  customLists$: Rx.Observable<readonly DiscoverCollection[]>;

  private INITIAL_PAGE_SIZE = 100;
  private limitSubject$ = new Rx.BehaviorSubject<number>(
    this.INITIAL_PAGE_SIZE,
  );
  private customListsCollection: DatastoreCollection<DiscoverCollectionsCollection>;

  constructor(private auth: Auth, private datastore: Datastore) {}

  ngOnInit() {
    this.listCurator$ = this.auth
      .getUserId()
      .pipe(map(userId => toNumber(userId)));

    const favoritesListDocument = this.datastore.document<PredefinedDiscoverCollectionsCollection>(
      'predefinedDiscoverCollections',
      DiscoverCollectionTypeApi.LISTS_FAVORITES,
      { index: 'type' },
    );

    const myHiresListCollection = this.datastore.collection<PredefinedDiscoverCollectionsCollection>(
      'predefinedDiscoverCollections',
      query =>
        this.hideMyHires
          ? Rx.of(query.null())
          : query.where('type', '==', DiscoverCollectionTypeApi.LISTS_MY_HIRES),
    );

    this.customListsCollection = this.datastore.collection<DiscoverCollectionsCollection>(
      'discoverCollections',
      query =>
        query
          .where('ownerId', '==', this.listCurator$)
          .where('type', '==', DiscoverCollectionTypeApi.LISTS_CUSTOM)
          .orderBy('name', OrderByDirection.ASC, ORDER_BY_OPTIONS_UNICODE_CI)
          .limit(this.limitSubject$.asObservable()),
    );

    this.customLists$ = this.customListsCollection.valueChanges();
    this.favoritesList$ = favoritesListDocument.valueChanges();
    this.myHiresList$ = myHiresListCollection
      .valueChanges()
      .pipe(map(myHiresListArray => myHiresListArray[0]));

    const favoritesListError$ = favoritesListDocument.status$.pipe(
      map(status => status.error),
    );

    const myHiresListError$ = myHiresListCollection.status$.pipe(
      map(status => status.error),
    );

    const customListsError$ = this.customListsCollection.status$.pipe(
      map(status => status.error),
    );

    const favoritesListLoading$ = Rx.combineLatest([
      this.favoritesList$.pipe(startWith(undefined)),
      favoritesListDocument.status$,
    ]).pipe(
      map(
        ([favoritesList, status]) =>
          !status.error && (!status.ready || !favoritesList),
      ),
    );

    const myHiresListLoading$ = Rx.combineLatest([
      this.myHiresList$.pipe(startWith(undefined)),
      myHiresListCollection.status$,
    ]).pipe(
      map(
        ([myHiresList, status]) =>
          !status.error &&
          (!status.ready || (!this.hideMyHires && !myHiresList)),
      ),
    );

    const customListsLoading$ = Rx.combineLatest([
      this.customListsCollection.status$,
      this.customLists$.pipe(startWith(undefined)),
    ]).pipe(
      map(
        ([status, customList]) =>
          !status.error && (!status.ready || !customList),
      ),
    );

    this.userListsLoading$ = Rx.combineLatest([
      favoritesListLoading$,
      myHiresListLoading$,
      customListsLoading$,
    ]).pipe(
      map(
        ([favoritesListLoading, myHiresListLoading, customListsLoading]) =>
          favoritesListLoading || myHiresListLoading || customListsLoading,
      ),
      startWith(true),
    );

    this.userListsError$ = Rx.combineLatest([
      favoritesListError$,
      myHiresListError$,
      customListsError$,
    ]).pipe(
      map(
        ([favoritesListError, myHiresListError, customListsError]) =>
          favoritesListError ?? myHiresListError ?? customListsError,
      ),
    );
  }

  handleClick(
    control: AbstractControl,
    discoverCollection: DiscoverCollection,
  ): void {
    if (this.listItemType === ListItemType.CHECKBOX) {
      control.disable();
    }

    this.select.emit(discoverCollection);

    if (this.listItemType === ListItemType.CHECKBOX) {
      control.enable();
    }
  }

  getFormControl(control: boolean | AbstractControl): FormControl {
    return control instanceof FormControl ? control : new FormControl(false);
  }
}
